import { parseISO, format, addDays } from 'date-fns';
import {
  IRowNode,
  ProcessRowGroupForExportParams,
  ShouldRowBeSkippedParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { MetricType } from 'components/common/types/DrilldownReports.types';
import { formatExportDateAndTime } from 'utils/formatExportDateAndTime';
import {
  AvailabilityReportFilter,
  AvailabilityReportData,
  AvailabilityDisplayFrame,
  AvailabilityDisplayWeek,
  AvailabilityGranularity,
  AvailabilityData,
  AvailabilityTotals,
  AvailabilitySummary,
  AvailabilityFrameAttributes,
} from './AvailabilityReport.types';

export const DAYS_IN_WEEK = 7;

const AVAILABILITY_VALUE_GETTERS = {
  [MetricType.IMPRESSIONS]: (availability: AvailabilityData) => availability.availableImpressions || [],
  [MetricType.FRAMES]: (availability: AvailabilityData) => availability.availableHits || [],
  [MetricType.SOT]: (availability: AvailabilityData) => availability.availableSot || [],
} as const;

export const getAvailabilityValues = (availability: AvailabilityData, metricType: string): (number | boolean)[] => {
  const getter = AVAILABILITY_VALUE_GETTERS[metricType as keyof typeof AVAILABILITY_VALUE_GETTERS];
  return getter ? getter(availability) : AVAILABILITY_VALUE_GETTERS[MetricType.IMPRESSIONS](availability);
};

const TOTAL_VALUE_GETTERS = {
  [MetricType.IMPRESSIONS]: (data: AvailabilityTotals) => data.totalAvailableImpressions || 0,
  [MetricType.FRAMES]: (data: AvailabilityTotals) => data.totalAvailableHits || 0,
  [MetricType.SOT]: (data: AvailabilityTotals) => data.avgAvailableSot || 0,
} as const;

export const getTotalValue = (data: AvailabilityTotals | AvailabilitySummary, metricType: string): number => {
  const getter = TOTAL_VALUE_GETTERS[metricType as keyof typeof TOTAL_VALUE_GETTERS];
  return getter ? getter(data) : TOTAL_VALUE_GETTERS[MetricType.IMPRESSIONS](data);
};

export const convertToNumeric = (value: number | boolean): number => {
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  return value;
};

const RANGE_VALUE_GETTERS = {
  [MetricType.IMPRESSIONS]: (summary: AvailabilitySummary) => summary.availableImpressions || [],
  [MetricType.FRAMES]: (summary: AvailabilitySummary) => summary.availableHits || [],
  [MetricType.SOT]: (summary: AvailabilitySummary) => summary.availableAvgSot || [],
} as const;

export const getRangeValues = (summary: AvailabilitySummary, metricType: string): (number | boolean)[] => {
  const getter = RANGE_VALUE_GETTERS[metricType as keyof typeof RANGE_VALUE_GETTERS];
  return getter ? getter(summary) : RANGE_VALUE_GETTERS[MetricType.IMPRESSIONS](summary);
};

export const hasMandatoryFilters = (filters: AvailabilityReportFilter): boolean => {
  const hasMediaType = filters.mediaType !== undefined && filters.mediaType !== null;
  const hasStartDate = filters.startDate !== undefined && filters.startDate !== null && filters.startDate !== '';
  const hasEndDate = filters.endDate !== undefined && filters.endDate !== null && filters.endDate !== '';
  const hasChannels =
    filters.channels !== undefined &&
    filters.channels !== null &&
    Array.isArray(filters.channels) &&
    filters.channels.length > 0;

  return hasMediaType && hasStartDate && hasEndDate && hasChannels;
};

export const calculateDate = (startDate: string, offset: number): string =>
  format(addDays(parseISO(startDate), offset), 'yyyy-MM-dd');

export const calculateAvailabilityWeekStartDate = (originalStartDate: Date, weekIndex: number): Date => {
  return weekIndex === 0 ? originalStartDate : addDays(originalStartDate, 5 + (weekIndex - 1) * 7);
};

export const calculateWeekStartDate = (startDate: string, weekIndex: number): string => {
  const start = parseISO(startDate);

  if (weekIndex === 0) {
    return startDate;
  }

  const startDayOfWeek = start.getDay();
  const daysToMonday = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
  const firstMonday = addDays(start, -daysToMonday + 7);

  const targetWeekStart = addDays(firstMonday, (weekIndex - 1) * DAYS_IN_WEEK);

  return format(targetWeekStart, 'yyyy-MM-dd');
};

const getStableNetworkKey = (networkValues: string[] | null | undefined): string | undefined => {
  if (!networkValues?.length) return undefined;

  return [...networkValues].sort().join(', ');
};

export const createBaseDisplayFrame = (
  frameId: string,
  attributes: AvailabilityFrameAttributes,
  totalValue: number,
): Omit<AvailabilityDisplayFrame, 'availability'> => {
  const networkKey = getStableNetworkKey(
    attributes.networkNames?.length ? attributes.networkNames : attributes.networkCodes,
  );

  return {
    frameId,
    productFormat: {
      name: attributes.productFormatName || attributes.productFormatCode,
      code: attributes.productFormatCode,
    },
    businessArea: {
      name: attributes.businessAreaName || attributes.businessAreaCode,
      code: attributes.businessAreaCode,
    },
    revenueStream: {
      name: attributes.revenueStreamName || attributes.revenueStreamCode,
      code: attributes.revenueStreamCode,
    },
    size: {
      name: attributes.frameSizeName || attributes.sizeCode,
      code: attributes.sizeCode,
    },
    district: {
      name: attributes.districtName ?? attributes.districtCode ?? null,
      code: attributes.districtCode ?? null,
    },
    ...(networkKey ? { network: { name: networkKey, code: networkKey } } : {}),
    town: {
      name: attributes.townName ?? attributes.townCode ?? null,
      code: attributes.townCode ?? null,
    },
    total: totalValue,
  };
};

export const createAvailabilityData = (
  availabilityValues: (number | boolean)[],
  startDate: string,
  granularity: AvailabilityGranularity,
  totalValue: number,
  targetWeekIndex?: number,
): AvailabilityDisplayWeek[] => {
  switch (granularity) {
    case AvailabilityGranularity.WEEK:
      return availabilityValues.map((value, weekIndex) => ({
        availabilityWeek: weekIndex,
        total: convertToNumeric(value),
        days: [
          {
            date: calculateDate(startDate, weekIndex * DAYS_IN_WEEK),
            total: convertToNumeric(value),
          },
        ],
      }));

    case AvailabilityGranularity.DAY: {
      const weekStartDate = calculateWeekStartDate(startDate, targetWeekIndex ?? 0);
      return [
        {
          availabilityWeek: targetWeekIndex ?? 0,
          total: totalValue,
          days: availabilityValues.map((value, index) => ({
            date: calculateDate(weekStartDate, index),
            total: convertToNumeric(value),
          })),
        },
      ];
    }

    case AvailabilityGranularity.HOUR: {
      const hourWeekStartDate = calculateWeekStartDate(startDate, targetWeekIndex ?? 0);
      return [
        {
          availabilityWeek: targetWeekIndex ?? 0,
          total: totalValue,
          days: [
            {
              date: hourWeekStartDate,
              total: totalValue,
              hours: availabilityValues.map((value, hourIndex) => ({
                hour: hourIndex,
                total: convertToNumeric(value),
              })),
            },
          ],
        },
      ];
    }

    default:
      return [];
  }
};

export const transformAvailabilityToDisplayFormat = (
  availabilityData: AvailabilityReportData,
  metricType: string,
  targetWeekIndex?: number,
): AvailabilityDisplayFrame[] => {
  const { granularity, startDate } = availabilityData.metadata;

  return availabilityData.frames.map((frame) => {
    const { frameId, attributes, availability, totals } = frame;
    const availabilityValues = getAvailabilityValues(availability, metricType);
    const totalValue = getTotalValue(totals, metricType);

    const baseDisplayFrame = createBaseDisplayFrame(frameId, attributes, totalValue);
    const displayAvailability = createAvailabilityData(
      availabilityValues,
      startDate,
      granularity,
      totalValue,
      targetWeekIndex,
    );

    return {
      ...baseDisplayFrame,
      availability: displayAvailability,
    };
  });
};

export const compareAvailabilityFrameColumn = (
  valueA: string | null | undefined,
  valueB: string | null | undefined,
  nodeA?: IRowNode,
  nodeB?: IRowNode,
): number => {
  const left = nodeA?.group ? nodeA.key : valueA;
  const right = nodeB?.group ? nodeB.key : valueB;

  if (left == null && right == null) {
    return 0;
  }
  if (left == null) {
    return -1;
  }
  if (right == null) {
    return 1;
  }
  if (left > right) {
    return 1;
  }
  if (left < right) {
    return -1;
  }

  return 0;
};

export const shouldSkipCollapsedAvailabilityExportRow = ({ node }: ShouldRowBeSkippedParams): boolean => {
  let { parent } = node;

  while (parent && parent.level >= 0) {
    if (!parent.expanded) {
      return true;
    }

    parent = parent.parent;
  }

  return false;
};

export const getAvailabilityExportGroupLabel = ({ node }: ProcessRowGroupForExportParams): string => node.key || '-';

export const getAvailabilityExportFileName = (name: string, extension: 'csv' | 'xlsx'): string => {
  const [date, time] = formatExportDateAndTime(new Date()).split('T_');
  const hoursAndMinutes = time.slice(0, 5);

  return `${name}_${date}T_${hoursAndMinutes}.${extension}`;
};

export const getAvailabilityTotal = (params: ValueGetterParams, weekNumber: number, dayIndex?: number): number => {
  const availability = params.data?.availability?.find(
    (avail: AvailabilityDisplayWeek) => avail.availabilityWeek === weekNumber,
  );

  if (dayIndex !== undefined) {
    return availability?.days?.[dayIndex]?.total || 0;
  }

  return availability?.total || 0;
};
